import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { UsersThree, Trash, Crown } from "@phosphor-icons/react";

export function TeamCard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "recepcao" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/team").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/team/invite", form);
      toast.success(`Convite enviado para ${form.email}`);
      await navigator.clipboard?.writeText(r.data.link).catch(() => {});
      setForm({ email: "", name: "", role: "recepcao" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/team/${id}`); toast.success("Acesso removido"); load(); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <div className="card-crisp p-6 space-y-4" data-testid="team-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><UsersThree size={20} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold text-lg">Equipe (usuários extras)</h3></div>
        <span className="text-xs text-slate-500" data-testid="team-limit">{data.members.length}{data.limit !== null ? ` / ${data.limit}` : ""} usuário(s)</span>
      </div>
      <p className="text-xs text-slate-500"><strong>Recepção</strong>: cria e envia orçamentos, vê procedimentos. <strong>Gestor</strong>: tudo, exceto plano, equipe e configurações de custos.</p>
      {!data.can_manage ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2" data-testid="team-upgrade-notice">
          <Crown size={18} className="mt-0.5 shrink-0" /><span>Disponível a partir do plano Master. <Link to="/app/billing" className="underline font-semibold">Fazer upgrade</Link></span>
        </div>
      ) : (
        <form onSubmit={invite} className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
          <div><Label className="text-xs">Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="team-name" /></div>
          <div><Label className="text-xs">E-mail</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="team-email" /></div>
          <div><Label className="text-xs">Perfil</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1 w-36" data-testid="team-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(data.roles).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
          <Button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="team-invite">Convidar</Button>
        </form>
      )}
      {data.members.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {data.members.map((m) => (
            <li key={m.id} className="py-2 flex items-center justify-between gap-3" data-testid={`team-member-${m.id}`}>
              <div><span className="font-medium text-slate-900">{m.name}</span> <span className="text-slate-500">· {m.email}</span></div>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{data.roles[m.role]}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{m.status === "active" ? "Ativo" : "Convidado"}</span>
                {data.can_manage && <button onClick={() => remove(m.id)} className="text-slate-400 hover:text-rose-600" data-testid={`team-remove-${m.id}`}><Trash size={16} /></button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function JoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/team/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e))); }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/team/accept", { token, password: info.needs_password ? password : null, current_password: info.needs_password ? null : password });
      localStorage.setItem("dp_token", r.data.token);
      toast.success("Bem-vindo(a) à equipe!");
      window.location.href = "/app";
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md card-crisp bg-white p-8" data-testid="join-page">
        {error ? <p className="text-slate-700">{error}</p> : !info ? <p className="text-slate-500">Carregando convite...</p> : (
          <form onSubmit={accept} className="space-y-4">
            <h1 className="font-heading text-2xl font-extrabold text-slate-900">Convite para {info.clinic}</h1>
            <p className="text-sm text-slate-600">Olá <strong>{info.name}</strong>, você foi convidado(a) como <strong>{info.role}</strong>. Acesso: {info.email}</p>
            <div><Label>{info.needs_password ? "Crie uma senha" : "Confirme a senha da sua conta"}</Label><Input type="password" minLength={info.needs_password ? 6 : 1} required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="join-password" /></div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="join-accept">Aceitar convite e entrar</Button>
            {!info.needs_password && <p className="text-xs text-slate-500">Você já tem conta com este e-mail; confirme sua senha e ela passará a acessar os dados desta clínica.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
